import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Bell,
  ChevronRight,
  FileSpreadsheet,
  HelpCircle,
  Home,
  Menu,
  Moon,
  PanelLeftOpen,
  Settings,
  Star,
  Sun,
} from "lucide-react";
import API from "../../../services/api";
import { AuthContext } from "../../Context/AuthContext";
import OrganisationLogo from "../../../components/common/OrganisationLogo";
import UserDropdown from "../../../components/user/UserDropdown";
import { ORG_BRANDING_UPDATED_EVENT } from "../../../utils/orgBranding";
import ReportsNavigationPanel from "./ReportsNavigationPanel";

export default function ReportsEnterpriseShell({
  categoryOrder,
  reportsByCategory,
  selectedKey,
  selectedReport,
  reportSearch,
  onReportSearchChange,
  favorites,
  onToggleFavorite,
  favReports,
  onSelectReport,
  onThemeChange,
  theme = "light",
  moduleTitle = "Reports",
  children,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useContext(AuthContext);
  const [org, setOrg] = useState(null);
  const [orgLogoVersion, setOrgLogoVersion] = useState(() => Date.now());
  const [user, setUser] = useState(null);
  const [navCollapsed, setNavCollapsed] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [viewport, setViewport] = useState({ isMobile: false, isTablet: false });
  const shellRef = useRef(null);

  useEffect(() => {
    API.get("/users/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
    API.get("/organisation/me")
      .then((r) => setOrg(r.data))
      .catch(() => setOrg(null));
  }, []);

  useEffect(() => {
    const onBranding = () => setOrgLogoVersion(Date.now());
    window.addEventListener(ORG_BRANDING_UPDATED_EVENT, onBranding);
    return () => window.removeEventListener(ORG_BRANDING_UPDATED_EVENT, onBranding);
  }, []);

  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth || 0;
      setViewport({ isMobile: w <= 768, isTablet: w > 768 && w <= 1024 });
    };
    compute();
    window.addEventListener("resize", compute, { passive: true });
    return () => window.removeEventListener("resize", compute);
  }, []);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname, location.search]);

  // While the reports drawer is open on mobile, hide the app's floating ☰
  // (Layout.jsx) so it doesn't sit on top of the drawer header.
  useEffect(() => {
    const open = viewport.isMobile && mobileNavOpen;
    document.body.classList.toggle("prw-nav-open", open);
    return () => document.body.classList.remove("prw-nav-open");
  }, [viewport.isMobile, mobileNavOpen]);

  const isItemActive = useCallback(
    (item) => {
      if (!item) return false;
      if (selectedKey && item.key === selectedKey) return true;
      if (item.href && selectedReport?.href) {
        const [itemPath, itemQuery] = item.href.split("?");
        const [selPath, selQuery] = (selectedReport.href || "").split("?");
        if (itemPath === selPath) {
          if (!itemQuery || !selQuery) return item.href === selectedReport.href;
          return item.href === selectedReport.href;
        }
      }
      return false;
    },
    [selectedKey, selectedReport]
  );

  const handleSelect = (report) => {
    onReportSearchChange("");
    onSelectReport(report);
  };

  const breadcrumbCategory = useMemo(() => {
    if (!selectedReport?.category) return null;
    return selectedReport.category;
  }, [selectedReport]);

  const showNavToggle = viewport.isMobile || viewport.isTablet;
  const navCollapsedEffective = viewport.isTablet ? navCollapsed : false;

  return (
    <>
      <header className="prw-shell" ref={shellRef} data-theme={theme}>
        <div className="prw-topbar">
          <div className="prw-topbar__left">
            {showNavToggle && (
              <button
                type="button"
                className="prw-topbar__nav-toggle"
                onClick={() => {
                  if (viewport.isMobile) setMobileNavOpen((v) => !v);
                  else setNavCollapsed((v) => !v);
                }}
                aria-label="Toggle reports navigation"
                title="Browse reports"
              >
                {/* Distinct from the app menu (☰) that floats beside it on phones. */}
                {viewport.isMobile ? <PanelLeftOpen size={18} /> : <Menu size={18} />}
              </button>
            )}
            <button type="button" className="prw-topbar__brand" onClick={() => navigate("/dashboard")}>
              <OrganisationLogo
                logoUrl={org?.logo_url}
                cacheBust={orgLogoVersion}
                className="prw-topbar__logo"
              />
              <span className="prw-topbar__brand-text">
                <strong>{org?.name || "Brixigo"}</strong>
                <small>HR &amp; Payroll Suite</small>
              </span>
            </button>
            <div className="prw-topbar__divider" aria-hidden />
            <div className="prw-topbar__module">
              <FileSpreadsheet size={18} />
              <span>{moduleTitle}</span>
            </div>
          </div>

          <nav className="prw-breadcrumb" aria-label="Breadcrumb">
            <button type="button" onClick={() => navigate("/dashboard")} className="prw-breadcrumb__link">
              <Home size={14} /> Home
            </button>
            <ChevronRight size={14} className="prw-breadcrumb__sep" />
            <button type="button" onClick={() => navigate("/reports/payroll")} className="prw-breadcrumb__link">
              Reports
            </button>
            {breadcrumbCategory && (
              <>
                <ChevronRight size={14} className="prw-breadcrumb__sep" />
                <span className="prw-breadcrumb__muted">{breadcrumbCategory}</span>
              </>
            )}
            <ChevronRight size={14} className="prw-breadcrumb__sep" />
            <span className="prw-breadcrumb__current">
              {selectedReport?.name || (!selectedKey ? "Getting started" : moduleTitle)}
            </span>
          </nav>

          <div className="prw-topbar__actions">
            <button type="button" className="prw-topbar__icon-btn" title="Notifications" aria-label="Notifications">
              <Bell size={18} />
            </button>
            <button
              type="button"
              className="prw-topbar__icon-btn"
              title="Help"
              aria-label="Help"
              onClick={() => navigate("/help")}
            >
              <HelpCircle size={18} />
            </button>
            <button
              type="button"
              className="prw-topbar__icon-btn"
              title="Settings"
              aria-label="Settings"
              onClick={() => navigate("/settings")}
            >
              <Settings size={18} />
            </button>
            <button
              type="button"
              className="prw-topbar__icon-btn"
              title={theme === "light" ? "Dark mode" : "Light mode"}
              aria-label="Toggle theme"
              onClick={() => onThemeChange(theme === "light" ? "dark" : "light")}
            >
              {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <UserDropdown
              me={user}
              onLogout={() => {
                logout();
                navigate("/login");
              }}
              orgName={org?.name}
              orgLogoUrl={org?.logo_url}
              orgLogoVersion={orgLogoVersion}
            />
          </div>
        </div>

        {favReports.length > 0 && (
          <motion.div
            className="prw-pinned"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
          >
            <Star size={14} className="prw-pinned__icon" />
            <span className="prw-pinned__label">Pinned</span>
            <div className="prw-pinned__list">
              {favReports.map((r) => {
                const Icon = r.icon || FileSpreadsheet;
                return (
                  <button
                    key={r.key}
                    type="button"
                    className={`prw-pinned__chip ${isItemActive(r) ? "is-active" : ""}`}
                    onClick={() => handleSelect(r)}
                  >
                    <Icon size={14} />
                    {r.name}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </header>

      <div className="prw-explorer" data-theme={theme}>
        <ReportsNavigationPanel
          categoryOrder={categoryOrder}
          reportsByCategory={reportsByCategory}
          selectedKey={selectedKey}
          selectedReport={selectedReport}
          reportSearch={reportSearch}
          onReportSearchChange={onReportSearchChange}
          favorites={favorites}
          onToggleFavorite={onToggleFavorite}
          onSelectReport={onSelectReport}
          isItemActive={isItemActive}
          mobileOpen={viewport.isMobile && mobileNavOpen}
          onCloseMobile={() => setMobileNavOpen(false)}
          collapsed={navCollapsedEffective}
          onToggleCollapsed={viewport.isTablet ? () => setNavCollapsed((v) => !v) : undefined}
          theme={theme}
        />
        <div className="prw-explorer__main">{children}</div>
      </div>
    </>
  );
}
